import { useEffect, useId, type ComponentProps, type FC, type ReactElement, type ReactNode, type Ref } from "react";
import {
  Controller,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseControllerProps,
  type UseFormSetFocus,
} from "react-hook-form";
import MuiDialog, { type DialogProps as MuiDialogProps } from "@mui/material/Dialog";
import {
  Collapse as MuiCollapse,
  Fade,
  DialogTitle,
  Stack,
  FormHelperText,
  Typography,
  Box,
  DialogContent,
  DialogActions,
  Button,
  TextField as MuiTextField,
} from "@mui/material";
import type { BoxProps, DialogActionsProps, StackProps, SxProps, TextFieldProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import { AlertCircleIcon } from "./icons";
import { customScrollbar } from "./customScrollbar";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";

const DIALOG_TITLE_ID = "dialog-title";

export const useAutoFocusField = <T extends FieldValues>(setFocus: UseFormSetFocus<T>, name: Path<T> | null) => {
  useEffect(() => {
    if (!name) return;
    const timeout = setTimeout(() => setFocus(name));
    return () => clearTimeout(timeout);
  }, [setFocus, name]);
};

export const Dialog = ({
  children,
  isOpen,
  maxWidth,
  sx,
}: {
  children: ReactNode;
  isOpen: boolean;
  maxWidth?: string;
  sx?: MuiDialogProps["sx"];
}) => {
  return (
    <MuiDialog
      transitionDuration={{ exit: 0 }}
      aria-labelledby={DIALOG_TITLE_ID}
      fullWidth
      open={isOpen}
      slotProps={{ paper: { sx: { maxWidth, ...sx } } }}
    >
      {children}
    </MuiDialog>
  );
};

export const Title: FC<{ children: ReactNode }> = ({ children }) => {
  return <DialogTitle id={DIALOG_TITLE_ID}>{children}</DialogTitle>;
};

export const Content: FC<{ children: ReactNode; sx?: SxProps }> = ({ children, sx }) => {
  return <DialogContent sx={{ ...customScrollbar, ...sx }}>{children}</DialogContent>;
};

export const Form: FC<ComponentProps<"form">> = ({ children, ...formProps }) => {
  return (
    <form
      style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
      autoComplete="off"
      {...formProps}
      onSubmit={e => {
        if (!formProps.onSubmit) return;
        e.preventDefault();
        e.stopPropagation();
        formProps.onSubmit(e);
      }}
    >
      {children}
    </form>
  );
};

export const FormError: FC<{ isError: boolean; errorMessage?: ReactNode }> = ({ isError, errorMessage }) => {
  const { t } = useTranslation();

  if (!isError) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <ErrorText isError text={errorMessage ?? t("FormDialog.GenericError")} />
    </Box>
  );
};

export const Fields: FC<{ children: ReactNode } & StackProps> = ({ children, ...props }) => {
  return (
    <Stack spacing={2.5} {...props}>
      {children}
    </Stack>
  );
};

export const Collapse: FC<{ children: ReactNode; isOpen: boolean; timeout?: number; onEntered?: () => void }> = ({
  children,
  isOpen,
  timeout = 500,
  onEntered,
}) => {
  return (
    <MuiCollapse
      in={isOpen}
      timeout={timeout}
      onEntered={onEntered}
      mountOnEnter
      unmountOnExit
      sx={{ "&&": { mt: 0, transition: "height" } }}
    >
      {children}
    </MuiCollapse>
  );
};

export const Reveal: FC<{ children: ReactElement; isOpen: boolean; onRevealed?: () => void }> = ({
  children,
  isOpen,
  onRevealed,
}) => {
  const { duration } = useTheme().transitions;

  return (
    <Collapse isOpen={isOpen} timeout={duration.short} onEntered={onRevealed}>
      <Fade
        in={isOpen}
        appear
        timeout={{ enter: duration.complex, exit: duration.shortest }}
        style={{ transitionDelay: isOpen ? `${duration.shortest}ms` : "0ms" }}
      >
        {children}
      </Fade>
    </Collapse>
  );
};

export const Field: FC<{
  children: ReactNode;
  errorMessage?: string | false;
  /** Id of the error text, for the input's `aria-describedby`. */
  errorId?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
}> = ({ children, errorMessage, errorId, onBlur }) => {
  return (
    <Stack onBlur={onBlur} sx={{ flex: 1, justifyContent: "flex-start", position: "relative" }}>
      {children}
      <Box sx={{ minHeight: "1lh" }}>{errorMessage && <ErrorText id={errorId} isError text={errorMessage} />}</Box>
    </Stack>
  );
};

export const Label: FC<{ children: ReactNode; htmlFor?: string; sx?: BoxProps["sx"] }> = ({ sx, ...props }) => {
  return (
    <Typography
      variant="body2"
      component="label"
      {...props}
      sx={[
        {
          pb: 1,
          fontWeight: "600",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

type ControllerBindingProps<T extends FieldValues, N extends Path<T>> = Pick<
  UseControllerProps<T, N>,
  "control" | "name" | "rules"
> & {
  label: ReactNode;
  /** Only for a stable selector (e.g. nova-cypress uses `#tenantId`). Defaults to a generated id. */
  id?: string;
};

/** Controller + Field + Label. `children` renders the control, linked to the label through `id`. */
export const ControlledField = <T extends FieldValues, N extends Path<T>>({
  control,
  name,
  rules,
  label,
  id: staticId,
  children,
}: ControllerBindingProps<T, N> & {
  children: (props: {
    field: ControllerRenderProps<T, N>;
    fieldState: ControllerFieldState;
    id: string;
    /** Set while there is an error: point the input's `aria-describedby` at it. */
    errorId: string | undefined;
  }) => ReactNode;
}) => {
  const generatedId = useId();
  const id = staticId ?? generatedId;
  const errorTextId = `${id}-error`;
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <Field errorMessage={fieldState.error?.message} errorId={errorTextId}>
          <Label htmlFor={id}>{label}</Label>
          {children({ field, fieldState, id, errorId: fieldState.error?.message ? errorTextId : undefined })}
        </Field>
      )}
    />
  );
};

export const TextField = <T extends FieldValues, N extends Path<T>>({
  control,
  name,
  rules,
  label,
  id,
  slotProps,
  ...textFieldProps
}: ControllerBindingProps<T, N> &
  // Owned by the Controller wiring below.
  Omit<TextFieldProps, "id" | "name" | "label" | "error" | "value" | "onChange" | "onBlur" | "inputRef">) => (
  <ControlledField control={control} name={name} rules={rules} label={label} id={id}>
    {({ field: { ref, ...field }, fieldState, id, errorId }) => (
      <MuiTextField
        {...field}
        value={field.value ?? ""}
        id={id}
        size="small"
        fullWidth
        error={Boolean(fieldState.error)}
        inputRef={ref}
        {...textFieldProps}
        slotProps={{ ...slotProps, htmlInput: { ...slotProps?.htmlInput, "aria-describedby": errorId } }}
      />
    )}
  </ControlledField>
);

const ErrorText: FC<{ id?: string; text?: ReactNode; isError: boolean }> = ({ id, isError, text }) => {
  if (!isError) return null;

  return (
    <FormHelperText
      component="div"
      id={id}
      error
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: ({ typography }) => typography.pxToRem(12),
        fontStyle: "normal",
      }}
    >
      <AlertCircleIcon size={14} color={theme => theme.palette.error.main} />
      <HACK_alignIconWithLabelBox>{text}</HACK_alignIconWithLabelBox>
    </FormHelperText>
  );
};

export const ActionButtons: FC<DialogActionsProps> = ({ children, ...props }) => {
  return <DialogActions {...props}>{children}</DialogActions>;
};

export const SubmitButton: FC<{
  children: ReactNode;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
}> = ({ children, isSubmitting, isDisabled, ref }) => {
  return (
    <Button ref={ref} type="submit" loading={isSubmitting} disabled={isDisabled}>
      {children}
    </Button>
  );
};

export const CancelButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <Button variant="outlined" onClick={onClick}>
      {t("FormDialog.CancelButton")}
    </Button>
  );
};
